var _excluded = ["actions", "disabled", "error", "errorMessage", "fieldSize", "fieldWidth", "hideValue", "icon", "iconColor", "id", "intensity", "label", "labelledBy", "message", "name", "placeholder", "prefix", "readOnly", "required", "step", "suffix", "tabIndex", "tooltip", "type", "value"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import * as IconSet from "../../../icons/index.js";
import Button from "../../basic/Button/Button.js";
import Icon from "../../basic/Icon/Icon.js";
import Tooltip from "../../basic/Tooltip/Tooltip.js";
import FieldError from "../FieldError/FieldError.js";
import FieldLabel from "../FieldLabel/FieldLabel.js";
import FieldMessage from "../FieldMessage/FieldMessage.js";
var getFieldPadding = (fieldSize, icon, $prefixWidthVal, $suffixWidthVal, $actionsWidthVal, $tooltipWidthVal) => {
  var iconWidth = 20;
  var left = 15;
  var right = 15;
  var vertical = 6;
  if (fieldSize === 'small') {
    iconWidth = 16;
    left = 9;
    right = 9;
    vertical = 3;
  }
  if (fieldSize === 'large') {
    iconWidth = 24;
    left = 18;
    right = 18;
    vertical = 12;
  }
  if (icon) {
    left += 6 + iconWidth;
  } else if ($prefixWidthVal) {
    left += 6 + $prefixWidthVal;
  }

  // Calculate right padding based on tooltip, suffix, and actions
  if ($actionsWidthVal) {
    right += 6 + $actionsWidthVal;
    if ($tooltipWidthVal) {
      right += 6 + $tooltipWidthVal; // Add space for tooltip before actions
    }
  } else if ($suffixWidthVal) {
    right += 6 + $suffixWidthVal;
    if ($tooltipWidthVal) {
      right += 6 + $tooltipWidthVal; // Add space for tooltip before suffix
    }
  } else if ($tooltipWidthVal) {
    right += 6 + $tooltipWidthVal; // Only tooltip on the right
  }
  return {
    left: "".concat(left, "px"),
    right: "".concat(right, "px"),
    all: "".concat(vertical, "px ").concat(right, "px ").concat(vertical, "px ").concat(left, "px")
  };
};

/* ICON */

var InputIcon = styled(Icon).withConfig({
  componentId: "CL__sc-i2ok9c-0"
})(["pointer-events:none;"]);
var StyledInputIconContainer = styled.div.withConfig({
  componentId: "CL__sc-i2ok9c-1"
})(["display:flex;align-items:center;justify-content:center;height:100%;position:absolute;top:0;", ""], _ref => {
  var {
    fieldSize
  } = _ref;
  return "left: ".concat(getFieldPadding(fieldSize).left);
});

/* PREFIX / SUFFIX */

var StyledPrefixSuffixContainer = styled.div.withConfig({
  componentId: "CL__sc-i2ok9c-2"
})(["display:flex;align-items:center;justify-content:center;height:100%;position:absolute;top:0;color:var(--border-color);font:", ";", ""], _ref2 => {
  var {
    fieldSize
  } = _ref2;
  return fieldSize === 'small' ? 'var(--body-text-x-small)' : 'var(--body-text-small)';
}, _ref3 => {
  var {
    fieldSize,
    textPosition
  } = _ref3;
  return "".concat(textPosition, ": ").concat(getFieldPadding(fieldSize)[textPosition]);
});
var StyledActionsContainer = styled.div.withConfig({
  componentId: "CL__sc-i2ok9c-3"
})(["align-items:center;display:flex;gap:var(--spacing-1);height:100%;justify-content:center;position:absolute;top:0;", ""], _ref4 => {
  var {
    fieldSize
  } = _ref4;
  return "right: ".concat(getFieldPadding(fieldSize).right);
});

/* TOOLTIP */

var StyledTooltip = styled(Tooltip).withConfig({
  componentId: "CL__sc-i2ok9c-4"
})(["max-width:240px;white-space:normal;word-break:break-word;"]);
var TooltipPortal = _ref5 => {
  var {
    children
  } = _ref5;
  return /*#__PURE__*/createPortal(children, document.body);
};
var StyledTooltipContainer = styled.div.withConfig({
  componentId: "CL__sc-i2ok9c-5"
})(["display:flex;align-items:center;justify-content:center;height:100%;position:absolute;top:0;right:", ";"], _ref6 => {
  var {
    fieldSize,
    actionsWidth,
    suffixWidth
  } = _ref6;
  // Get base padding
  var basePadding = parseInt(getFieldPadding(fieldSize).right, 10);

  // If there are actions or suffix, position the tooltip before them
  if (actionsWidth) {
    return "calc(".concat(basePadding, "px + ").concat(actionsWidth, "px + var(--spacing-1))");
  }
  if (suffixWidth) {
    return "calc(".concat(basePadding, "px + ").concat(suffixWidth, "px + var(--spacing-1))");
  }

  // Otherwise use default padding
  return "".concat(basePadding, "px");
});

/* INPUT SIZIFIER */

var StyledInputSizifier = styled.div.withConfig({
  componentId: "CL__sc-i2ok9c-6"
})(["position:relative;width:", ";"], _ref7 => {
  var {
    fieldWidth
  } = _ref7;
  return fieldWidth === 'full' ? '100%' : "".concat(fieldWidth, "px");
});

/* INPUT */

var StyledInput = styled.input.withConfig({
  componentId: "CL__sc-i2ok9c-7"
})(["appearance:none;display:block;background:", ";color:var(--text-color);position:relative;box-shadow:none;outline:none;width:100%;border:1px solid ", ";border-radius:var(--border-radius-x-small);transition:border-color var(--hover-transition);padding:", ";", " ", " ", " &:not(:disabled):hover{box-shadow:none;border-color:var(--highlight-color);}&:not(:disabled):focus-visible{box-shadow:0 0 5px var(--highlight-color);border-color:var(--highlight-color);}::placeholder{color:var(--gray-5);opacity:1;font:inherit;}&::selection{background:var(--highlight-color);color:var(--main-bkg-color);}&::-moz-focus-inner{margin-top:-2px;margin-bottom:-2px;border:0;padding:0;}&:-webkit-autofill,&:-webkit-autofill:hover,&:-webkit-autofill:focus-visible{box-shadow:0 0 0 1000px var(--main-bkg-color) inset;}", " ", " ", " ", " ", ""], _ref8 => {
  var {
    intensity
  } = _ref8;
  return intensity === 'light' ? 'var(--surface-bkg-color)' : 'var(--main-bkg-color)';
}, _ref9 => {
  var {
    intensity
  } = _ref9;
  return intensity === 'light' ? 'var(--border-color-dim)' : 'var(--border-color)';
}, _ref0 => {
  var {
    fieldSize,
    icon,
    $prefixWidthVal,
    $suffixWidthVal,
    $actionsWidthVal,
    $tooltipWidthVal
  } = _ref0;
  return fieldSize && getFieldPadding(fieldSize, icon, $prefixWidthVal, $suffixWidthVal, $actionsWidthVal, $tooltipWidthVal).all;
}, _ref1 => {
  var {
    fieldSize
  } = _ref1;
  return fieldSize === 'large' ? "\n      height: var(--spacing-8);\n      font: var(--body-text-medium);\n    " : "";
}, _ref10 => {
  var {
    fieldSize
  } = _ref10;
  return fieldSize === 'medium' ? "\n      height: var(--spacing-6);\n      font: var(--body-text-small);\n    " : "";
}, _ref11 => {
  var {
    fieldSize
  } = _ref11;
  return fieldSize === 'small' ? "\n      height: var(--spacing-4);\n      font: var(--body-text-x-small);\n    " : "";
}, _ref12 => {
  var {
    error
  } = _ref12;
  return error && "\n    border-color: var(--error-color);\n\n    &:not(:disabled):hover {\n      border-color: var(--error-color);\n    }\n\n    &:not(:disabled):focus-visible {\n      box-shadow: 0 0 5px var(--error-color);\n      border-color: var(--error-color);\n    }\n  ";
}, _ref13 => {
  var {
    disabled
  } = _ref13;
  return disabled && "\n    background-color: var(--surface-bkg-color);\n    border-color: var(--border-color-dim);\n  ";
}, _ref14 => {
  var {
    error,
    readOnly
  } = _ref14;
  return readOnly && !error && css(["border-color:var(--border-color-extra-dim);"]);
}, _ref15 => {
  var {
    readOnly
  } = _ref15;
  return readOnly && css(["color:var(--text-color-dim);"]);
}, _ref16 => {
  var {
    hideValue
  } = _ref16;
  return hideValue && css(["caret-color:transparent;color:transparent !important;overflow:hidden;pointer-events:none;text-overflow:clip;user-select:none;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;&::selection{background:transparent;}&::-moz-selection{background:transparent;}"]);
});
var Input = /*#__PURE__*/React.forwardRef((_ref17, ref) => {
  var {
      actions,
      disabled = false,
      error = false,
      errorMessage = "There's an error with this field",
      fieldSize = 'medium',
      fieldWidth = 'full',
      hideValue = false,
      icon = undefined,
      iconColor,
      id = '',
      intensity = 'default',
      label,
      labelledBy,
      message,
      name = '',
      placeholder = '',
      prefix,
      readOnly = false,
      required = false,
      step,
      suffix = '',
      tabIndex = 0,
      tooltip,
      type = 'text',
      value
    } = _ref17,
    props = _objectWithoutProperties(_ref17, _excluded);
  var prefixRef = useRef(null);
  var suffixRef = useRef(null);
  var actionsRef = useRef(null);
  var tooltipRef = useRef(null);
  var [prefixWidth, setPrefixWidth] = useState(0);
  var [suffixWidth, setSuffixWidth] = useState(0);
  var [actionsWidth, setActionsWidth] = useState(0);
  var [tooltipWidth, setTooltipWidth] = useState(0);
  useEffect(() => {
    var _prefixRef$current, _suffixRef$current, _actionsRef$current, _tooltipRef$current;
    if (prefixRef !== null && prefixRef !== void 0 && (_prefixRef$current = prefixRef.current) !== null && _prefixRef$current !== void 0 && _prefixRef$current.offsetWidth) {
      setPrefixWidth(prefixRef.current.offsetWidth);
    } else {
      setPrefixWidth(0);
    }
    if (suffixRef !== null && suffixRef !== void 0 && (_suffixRef$current = suffixRef.current) !== null && _suffixRef$current !== void 0 && _suffixRef$current.offsetWidth) {
      setSuffixWidth(suffixRef.current.offsetWidth);
    } else {
      setSuffixWidth(0);
    }
    if (actionsRef !== null && actionsRef !== void 0 && (_actionsRef$current = actionsRef.current) !== null && _actionsRef$current !== void 0 && _actionsRef$current.offsetWidth) {
      setActionsWidth(actionsRef.current.offsetWidth);
    } else {
      setActionsWidth(0);
    }
    if (tooltipRef !== null && tooltipRef !== void 0 && (_tooltipRef$current = tooltipRef.current) !== null && _tooltipRef$current !== void 0 && _tooltipRef$current.offsetWidth) {
      setTooltipWidth(tooltipRef.current.offsetWidth);
    } else {
      setTooltipWidth(0);
    }
  }, [prefix, suffix, actions, tooltip]);
  return /*#__PURE__*/React.createElement("div", null, label && /*#__PURE__*/React.createElement(FieldLabel, {
    disabled: disabled,
    fieldSize: fieldSize,
    htmlFor: id,
    intensity: intensity,
    readOnly: readOnly,
    required: required
  }, label), /*#__PURE__*/React.createElement(StyledInputSizifier, {
    fieldWidth: fieldWidth
  }, /*#__PURE__*/React.createElement(StyledInput, _extends({
    $actionsWidthVal: actions ? actionsWidth : undefined,
    "aria-label": label,
    "aria-labelledby": labelledBy,
    "aria-disabled": hideValue ? true : undefined,
    disabled: disabled,
    error: error,
    fieldSize: fieldSize,
    fieldWidth: fieldWidth,
    hideValue: hideValue,
    icon: icon,
    id: id,
    intensity: intensity,
    name: name,
    placeholder: placeholder,
    $prefixWidthVal: prefixWidth,
    readOnly: readOnly || hideValue,
    ref: ref,
    required: required,
    step: step,
    suffix: suffix,
    $suffixWidthVal: suffixWidth,
    tabIndex: hideValue ? -1 : tabIndex,
    $tooltipWidthVal: tooltip ? tooltipWidth : undefined,
    type: type,
    value: value
  }, props)), icon && /*#__PURE__*/React.createElement(StyledInputIconContainer, {
    fieldSize: fieldSize
  }, /*#__PURE__*/React.createElement(InputIcon, {
    color: iconColor || 'currentColor',
    component: icon,
    size: fieldSize
  })), prefix && !icon && /*#__PURE__*/React.createElement(StyledPrefixSuffixContainer, {
    fieldSize: fieldSize,
    textPosition: "left",
    ref: prefixRef
  }, prefix), tooltip && /*#__PURE__*/React.createElement(StyledTooltipContainer, {
    fieldSize: fieldSize,
    actionsWidth: actionsWidth,
    suffixWidth: suffixWidth
  }, /*#__PURE__*/React.createElement(Icon, {
    component: IconSet.Information,
    size: "small",
    "aria-describedby": tooltip.id,
    "data-for": tooltip.id,
    "data-tip": true,
    ref: tooltipRef
  }), /*#__PURE__*/React.createElement(TooltipPortal, null, /*#__PURE__*/React.createElement(StyledTooltip, _extends({
    size: "small"
  }, tooltip), tooltip.children))), suffix && !actions && /*#__PURE__*/React.createElement(StyledPrefixSuffixContainer, {
    fieldSize: fieldSize,
    textPosition: "right",
    ref: suffixRef
  }, suffix), actions && /*#__PURE__*/React.createElement(StyledActionsContainer, {
    fieldSize: fieldSize,
    ref: actionsRef
  }, (actions === null || actions === void 0 ? void 0 : actions.actionOne) && /*#__PURE__*/React.createElement(Button, _extends({}, actions.actionOne, {
    size: "small",
    variant: fieldSize === 'small' ? 'plain' : actions.actionOne.variant
  }), actions.actionOne.label), (actions === null || actions === void 0 ? void 0 : actions.actionTwo) && /*#__PURE__*/React.createElement(Button, _extends({}, actions.actionTwo, {
    size: "small",
    variant: fieldSize === 'small' ? 'plain' : actions.actionTwo.variant
  }), actions.actionTwo.label))), message && !error && /*#__PURE__*/React.createElement(FieldMessage, {
    fieldSize: fieldSize,
    htmlFor: id
  }, message), errorMessage && error && /*#__PURE__*/React.createElement(FieldError, {
    fieldSize: fieldSize,
    htmlFor: id
  }, errorMessage));
});
export default Input;